import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  Input,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { MatDialogClose, MatDialog } from '@angular/material/dialog';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import { EtJournalEntry, EtJournalSigner } from '@et/typings';
import { EjournalService } from '../../services/ejournal.service';
import { finalize } from 'rxjs';
import {
  JournalEntryAddEditComponent,
  JournalEntryAddEditComponentData,
} from '../journal-entry-add-edit/journal-entry-add-edit.component';
import {
  JournalEntryAddSupplementalComponent,
  JournalEntryAddSupplementalData,
} from '../journal-entry-add-supplemental/journal-entry-add-supplemental.component';
import { PreviewPdfBtnComponent } from '../preview-pdf-btn/preview-pdf-btn.component';

@Component({
  selector: 'et-journal-entry-card',
  imports: [
    DatePipe,
    CurrencyPipe,
    MatDialogClose,
    PresentationalAtomsModule,
    PreviewPdfBtnComponent,
  ],
  templateUrl: './journal-entry-card.component.html',
  styleUrl: './journal-entry-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JournalEntryCardComponent {
  @Input({ required: true }) entry!: EtJournalEntry;
  @Input({ required: true }) signers!: EtJournalSigner[];
  @Input({ required: true }) isFinalized!: boolean;

  @ViewChild('deleteEntryModal') deleteEntryModal!: TemplateRef<unknown>;

  private ejournalService = inject(EjournalService);

  private dialog = inject(MatDialog);
  private injector = inject(Injector);

  journalFees = computed(
    () => this.ejournalService.eJournal()?.feeCharged ?? 0,
  );

  /** True while a delete request is in flight. */
  isDeletingEntry = signal<boolean>(false);

  get isCancelAct(): boolean {
    return !!this.entry.cancelActUtcDate;
  }

  get isEntry(): boolean {
    return !this.entry.cancelActUtcDate;
  }

  /**
   * Signer whose `ejournalEntryIds` includes this entry, if any.
   */
  get linkedSigner(): EtJournalSigner | null {
    return (
      this.signers.find(
        (s) => s.ejournalEntryIds.indexOf(this.entry.eJournalEntryId) !== -1,
      ) || null
    );
  }

  /**
   * Opens the edit journal entry dialog.
   */
  onEditEntry(): void {
    const type = this.isCancelAct ? 'cancel_act' : 'entry';
    this.dialog.open<
      JournalEntryAddEditComponent,
      JournalEntryAddEditComponentData
    >(JournalEntryAddEditComponent, {
      injector: this.injector,
      panelClass: ['et-dialog-radius', 'et-dialog-padding'],
      autoFocus: false,
      width: '100%',
      maxWidth: '800px',
      maxHeight: '90vh',
      data: { entry: this.entry, type },
    });
  }

  /**
   * Opens the delete entry confirmation modal.
   */
  onDeleteEntry() {
    this.dialog.open(this.deleteEntryModal, {
      width: '100%',
      maxWidth: '500px',
      panelClass: ['et-dialog-radius', 'et-dialog-padding'],
      autoFocus: false,
      id: 'delete-entry-modal',
    });
  }

  /**
   * Deletes this entry via the API and closes the confirmation modal on success.
   */
  deleteEntry() {
    this.isDeletingEntry.set(true);
    this.ejournalService
      .deleteEntry(this.entry)
      .pipe(finalize(() => this.isDeletingEntry.set(false)))
      .subscribe(() => {
        this.dialog.getDialogById('delete-entry-modal')?.close();
      });
  }

  /**
   * Opens the dialog to add a supplemental note to this entry (finalized journals only).
   */
  onAddSupplementalEntry() {
    this.dialog.open<
      JournalEntryAddSupplementalComponent,
      JournalEntryAddSupplementalData
    >(JournalEntryAddSupplementalComponent, {
      injector: this.injector,
      panelClass: ['et-dialog-radius', 'et-dialog-padding'],
      autoFocus: false,
      width: '100%',
      maxWidth: '800px',
      maxHeight: '90vh',
      data: { entryId: this.entry.eJournalEntryId },
    });
  }
}
